<script setup>
import pdfViewer from '@/components/pdfViewer'
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { get_material, get_reading_materials } from '@/services/materials';


import axios from 'axios'

const moduleInf = ref({})
const reading_materials = ref([])
const route = useRoute()
const isLoading = ref(true)
const loadError = ref(null)
const downloadProgress = ref(0)
const pdfBlobUrl = ref(null)
const loadingMessage = ref('Initializing download...')
let messageInterval = null

const loadingMessages = [
  "Fetching your learning materials...",
  "Preparing the knowledge base...",
  "Almost there, just a few more bytes...",
  "Decrypting the secrets of the universe...",
  "Loading wisdom...",
  "Patience is a virtue (and a loading screen)..."
]

function startLoadingMessages() {
  let index = 0
  loadingMessage.value = loadingMessages[0]
  messageInterval = setInterval(() => {
    index = (index + 1) % loadingMessages.length
    loadingMessage.value = loadingMessages[index]
  }, 2000) // Change message every 2 seconds
}

function stopLoadingMessages() {
  if (messageInterval) {
    clearInterval(messageInterval)
    messageInterval = null
  }
}

async function fetchPdfWithProgress(url) {
  startLoadingMessages()
  try {
    const response = await axios.get(url, {
      responseType: 'blob',
      headers: {
        'Authorization': localStorage.getItem('uid')
      },
      onDownloadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        downloadProgress.value = percentCompleted
      }
    })
    
    const blob = new Blob([response.data], { type: 'application/pdf' })
    pdfBlobUrl.value = URL.createObjectURL(blob)
  } catch (error) {
    console.error('Error downloading PDF:', error)
    // Fallback to original URL if download fails, but this might fail auth checks if not public
    // pdfBlobUrl.value = url 
  } finally {
    stopLoadingMessages()
  }
}

onMounted(async ()=>{
  try {
    let module_id = route.params.id
    
    if (!module_id) {
      loadError.value = 'No module ID provided'
      isLoading.value = false
      return
    }
    
    let response = await get_material(module_id)
    
    if (response.error || !response.result?.data?.data) {
      console.error('Error loading module:', response)
      loadError.value = 'Failed to load module data'
      isLoading.value = false
      return
    }
    
    // Parse module data
    moduleInf.value = parseModule(response.result.data.data)
    
    // Load reading materials
    reading_materials.value = await parseReading(response.result.data.data.id)
    
    // Show page content immediately
    isLoading.value = false
    
    // Start PDF download in background (progress bar will show in accordion)
    if (moduleInf.value.attributes?.material_media) {
      await fetchPdfWithProgress(moduleInf.value.attributes.material_media)
    }
  } catch (error) {
    console.error('Exception loading module:', error)
    loadError.value = 'An unexpected error occurred'
    isLoading.value = false
  }
})

function parseModule(response){
  const tempData = {...response}
  
  // Safely parse reference_urls from description
  try {
    if (tempData.attributes?.description) {
      tempData.reference_urls = JSON.parse(tempData.attributes.description)
    } else {
      tempData.reference_urls = []
    }
  } catch (error) {
    console.error('Error parsing reference URLs:', error)
    tempData.reference_urls = []
  }
  
  return tempData
}

const showPdf = ref(false)
const currentReading = ref('')
async function parseReading(module_id){
  if (!module_id) return []
  
  try {
    let response = await get_reading_materials(module_id)
    
    // Check if response and nested properties exist
    if (!response || response.error || !response.result?.data?.data) {
      console.error('Error fetching reading materials:', response)
      return []
    }
    
    let tempReading = response.result.data.data.map(item => {
      return {
        ...item.attributes,
        id: item.id
      }
    })
    return tempReading
  } catch (error) {
    console.error('Exception in parseReading:', error)
    return []
  }
}

</script>

<template>
  <div class="container-fluid">
    <Breadcrumbs :main="moduleInf.attributes?.name + ' Module'"/>
    
    <!-- Loading State -->
    <div v-if="isLoading" class="card">
      <div class="card-body text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3 text-muted">Loading module...</p>
      </div>
    </div>
    
    <!-- Error State -->
    <div v-else-if="loadError" class="card">
      <div class="card-body text-center py-5">
        <vue-feather type="alert-circle" class="text-danger mb-3" size="48"></vue-feather>
        <h5 class="text-danger">{{ loadError }}</h5>
        <button class="btn btn-primary mt-3" @click="$router.go(-1)">Go Back</button>
      </div>
    </div>
    
    <!-- Content -->
    <div v-else class="mb-5">
      <div class="default-according" id="accordion">
        <div class="card">
          <div class="card-header" id="headingOne">
            <h5 class="mb-0">
              <button class="btn btn-link d-flex justify-content-between" data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded="true" aria-controls="collapseOne">
                <p class="mb-0">Module</p>
                <div class="btn icon-btn btn-light-primary">
                  <vue-feather type="chevron-right" class="me-0"/>
                </div>
              </button>
            </h5>
          </div>
          <div class="collapse show" id="collapseOne" aria-labelledby="headingOne" data-bs-parent="#accordion">
            <div class="card-body">
              <div v-if="downloadProgress < 100 && moduleInf.attributes?.material_media" class="text-center py-4">
                <div class="progress mb-2" style="height: 10px;">
                  <div class="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                       role="progressbar" 
                       :style="{ width: downloadProgress + '%' }" 
                       :aria-valuenow="downloadProgress" 
                       aria-valuemin="0" 
                       aria-valuemax="100">
                  </div>
                </div>
                <p class="text-muted mb-0">{{ loadingMessage }} {{ downloadProgress }}%</p>
              </div>
              <pdfViewer v-else-if="pdfBlobUrl" :pdf_url="pdfBlobUrl" />
              <div v-else class="text-center py-4 text-muted">
                No PDF module available
              </div>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-header" id="headingTwo">
            <h5 class="mb-0">
              <button class="btn btn-link collapsed d-flex justify-content-between" data-bs-toggle="collapse" data-bs-target="#collapseTwo" aria-expanded="false" aria-controls="collapseTwo">
                <p class="mb-0">Reading Material</p>
                <div class="btn icon-btn btn-light-primary">
                  <vue-feather type="chevron-right" class="me-0"/>
                </div>
              </button>
            </h5>
          </div>
          <div class="collapse" id="collapseTwo" aria-labelledby="headingTwo" data-bs-parent="#accordion">
            <div class="card-body" v-auto-animate>
              <div class="d-flex flex-wrap">
                <a v-for="item in reading_materials" 
                   :key="item.id"
                   :href="item.material_media || item.description" 
                   target="_blank"
                   class="btn btn-light-success mx-1 my-1"
                >
                  {{ item.name }}
                  <vue-feather type="external-link" size="14" class="ms-2"/>
                </a>
              </div>
              <div v-if="reading_materials.length === 0" class="text-center text-muted py-3">
                No reading materials available
              </div>
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-header" id="headingThree">
            <h5 class="mb-0">
              <button class="btn btn-link collapsed d-flex justify-content-between" data-bs-toggle="collapse" data-bs-target="#collapseThree" aria-expanded="false" aria-controls="collapseThree">
                <p class="mb-0">References</p>
                <div class="btn icon-btn btn-light-primary">
                  <vue-feather type="chevron-right" class="me-0"/>
                </div>
              </button>
            </h5>
          </div>
          <div class="collapse" id="collapseThree" aria-labelledby="headingThree" data-bs-parent="#accordion">
            <div class="card-body">
              <ul class="list-group">
                <li class="list-group-item" v-for="(item, i) in moduleInf.reference_urls">
                  <div class="d-flex align-items-center">
                    <span>
                      {{i+1}}. 
                      <a :href="item.url">
                        {{ item.name }}
                      </a>
                    </span>
                    <!-- <vue-feather type="external-link" /> -->
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
